import { useEffect, useState, useMemo, Fragment } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { Search, Plus, Pencil, Trash2, X, Save, ChevronDown, ChevronRight, Check, Circle, CircleDot, Share2, MapPin, Phone, Users } from "lucide-react";

const emptyNeed = { name: "", requested_qty: "0", fulfilled_qty: "0", status: "pending" };
const empty = { kabupaten: "", kota: "", pic_name: "", pic_number: "", people_count: 0, needs: [], details: "", help_provided: "", status: "pending" };

function StatusPill({ s, t, statuses, lang }) {
  const map = {
    helped: "bg-[#670003] text-[#fff0d2] border-[#670003]",
    in_progress: "bg-[#141414] text-[#fff0d2] border-[#141414]",
    pending: "bg-transparent text-[#141414] border-[#141414]",
  };
  const cls = map[s] || "bg-transparent text-[#141414] border-[#141414]";
  const meta = (statuses || []).find(x => x.key === s);
  const label = meta ? (lang === "id" ? meta.label_id : meta.label_en) : (t.beneficiaries.statuses[s] || s);
  return (
    <span className={`inline-flex items-center text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${cls}`}>
      {label}
    </span>
  );
}

function NeedStatusIcon({ s }) {
  if (s === "fulfilled") return <Check className="w-3.5 h-3.5 text-[#670003]" strokeWidth={3} />;
  if (s === "partial") return <CircleDot className="w-3.5 h-3.5 text-[#141414]" strokeWidth={2.5} />;
  return <Circle className="w-3.5 h-3.5 text-[#141414]/40" strokeWidth={2} />;
}

function ProgressBar({ value, tid }) {
  return (
    <div data-testid={tid} className="flex items-center gap-2">
      <div className="h-1.5 w-24 md:w-32 progress-track">
        <div className="h-full bg-[#670003]" style={{ width: `${value}%` }} />
      </div>
      <span className="text-xs font-mono w-10 text-right">{Math.round(value)}%</span>
    </div>
  );
}

export default function Beneficiaries() {
  const { t, user, settings, lang } = useApp();
  const [items, setItems] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortKey, setSortKey] = useState("progress");
  const [sortDir, setSortDir] = useState("asc"); // ascending = most in need first
  const [expanded, setExpanded] = useState({});
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const load = async (query = "") => {
    const r = await api.get(`/beneficiaries${query ? `?q=${encodeURIComponent(query)}` : ""}`);
    setItems(r.data);
  };
  useEffect(() => { load(); api.get("/categories/statuses").then(r => setStatuses(r.data)); }, []);
  useEffect(() => { const h = setTimeout(() => load(q), 300); return () => clearTimeout(h); }, [q]);

  const rows = useMemo(() => {
    let r = statusFilter === "all" ? items : items.filter((i) => i.status === statusFilter);
    r = [...r].sort((a, b) => {
      const av = a[sortKey] ?? 0, bv = b[sortKey] ?? 0;
      if (typeof av === "string") return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return r;
  }, [items, statusFilter, sortKey, sortDir]);

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir("asc"); }
  };

  const openNew = () => { setForm({ ...empty, needs: [{ ...emptyNeed }] }); setEditing("new"); };
  const openEdit = (item) => { setForm({ ...item, needs: (item.needs || []).map(n => ({ ...emptyNeed, ...n })) }); setEditing(item.id); };
  const close = () => { setEditing(null); setForm(empty); };

  const setNeed = (i, patch) => {
    const needs = [...form.needs];
    needs[i] = { ...needs[i], ...patch };
    setForm({ ...form, needs });
  };
  const addNeedRow = () => setForm({ ...form, needs: [...form.needs, { ...emptyNeed }] });
  const removeNeedRow = (i) => setForm({ ...form, needs: form.needs.filter((_, idx) => idx !== i) });

  const save = async () => {
    setLoading(true);
    try {
      const payload = {
        ...form,
        people_count: Number(form.people_count) || 0,
        needs: form.needs.filter((n) => n.name.trim()),
      };
      if (editing === "new") await api.post("/beneficiaries", payload);
      else await api.put(`/beneficiaries/${editing}`, payload);
      toast.success("Saved");
      close();
      load(q);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    setLoading(false);
  };

  const remove = async (id) => {
    if (!window.confirm(t.admin.confirmDelete)) return;
    await api.delete(`/beneficiaries/${id}`);
    toast.success("Deleted");
    load(q);
  };

  const th = (label, key, testid) => (
    <button onClick={() => toggleSort(key)} data-testid={`th-${testid}`} className="text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-[#141414]/70 hover:text-[#141414] flex items-center gap-1">
      {label}
      {sortKey === key && <span className="opacity-60">{sortDir === "asc" ? "↑" : "↓"}</span>}
    </button>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-2">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl tracking-tight">{settings?.[`warga_title_${lang}`] || t.beneficiaries.title}</h1>
          <p className="mt-2 text-[#141414]/70 max-w-xl text-sm">{settings?.[`warga_subtitle_${lang}`] || t.beneficiaries.subtitle}</p>
        </div>
        {user && (
          <button onClick={openNew} data-testid="beneficiary-add-btn" className="flat-btn-primary rounded-full px-5 py-2.5 font-semibold text-sm flex items-center gap-2 self-start">
            <Plus className="w-4 h-4" strokeWidth={2.5} /> {t.beneficiaries.add}
          </button>
        )}
      </div>

      {/* Transparency banner */}
      <div className="mt-4 mb-6 flat-border rounded-xl bg-white p-4 flex items-start gap-3">
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#670003] shrink-0 mt-1">Transparansi</div>
        <div className="text-sm text-[#141414]/80">{t.beneficiaries.transparency}</div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col md:flex-row gap-3 mb-4">
        <div className="flat-border rounded-full flex items-center px-4 py-2 gap-2 bg-white flex-1 min-w-0">
          <Search className="w-4 h-4 shrink-0" strokeWidth={2} />
          <input data-testid="beneficiaries-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t.search.placeholder} className="flex-1 bg-transparent outline-none text-sm min-w-0" />
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            data-testid="filter-all"
            onClick={() => setStatusFilter("all")}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold border ${
              statusFilter === "all" ? "bg-[#670003] text-[#fff0d2] border-[#670003]" : "bg-transparent border-[#141414] text-[#141414] hover:bg-[#141414] hover:text-[#fff0d2]"
            }`}
          >
            {t.beneficiaries.filterAll}
          </button>
          {statuses.map((s) => (
            <button
              key={s.key}
              data-testid={`filter-${s.key}`}
              onClick={() => setStatusFilter(s.key)}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold border ${
                statusFilter === s.key ? "bg-[#670003] text-[#fff0d2] border-[#670003]" : "bg-transparent border-[#141414] text-[#141414] hover:bg-[#141414] hover:text-[#fff0d2]"
              }`}
            >
              {lang === "id" ? s.label_id : s.label_en}
            </button>
          ))}
        </div>
      </div>

      {/* Mobile: card layout */}
      <div className="md:hidden space-y-3">
        {rows.length === 0 && (
          <div className="flat-border rounded-2xl bg-white text-center py-10 text-[#141414]/50 text-sm">{t.beneficiaries.empty}</div>
        )}
        {rows.map((b) => {
          const isOpen = !!expanded[b.id];
          return (
            <div key={b.id} data-testid={`card-${b.id}`} className="flat-border rounded-2xl bg-white overflow-hidden">
              <button
                type="button"
                onClick={() => setExpanded((e) => ({ ...e, [b.id]: !e[b.id] }))}
                className="w-full text-left p-4"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-[15px] leading-tight break-words">{b.pic_name}</div>
                    {b.pic_number && (
                      <div className="mt-0.5 flex items-center gap-1.5 text-[12px] text-[#141414]/70">
                        <Phone className="w-3 h-3 shrink-0" strokeWidth={2} />
                        <span className="break-all">{b.pic_number}</span>
                      </div>
                    )}
                  </div>
                  <div className="shrink-0 flex items-center gap-1.5">
                    <StatusPill s={b.status} t={t} statuses={statuses} lang={lang} />
                    {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </div>
                </div>

                <div className="mt-2.5 flex items-start gap-2 text-[13px]">
                  <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#141414]/60" strokeWidth={2} />
                  <span className="break-words">
                    <span className="font-medium">{b.kabupaten || "—"}</span>
                    {b.kota && <span className="text-[#141414]/70"> · {b.kota}</span>}
                  </span>
                </div>

                <div className="mt-2 flex items-center gap-3 text-[12px]">
                  <div className="flex items-center gap-1.5 text-[#141414]/70">
                    <Users className="w-3.5 h-3.5" strokeWidth={2} />
                    <span className="font-mono"><span className="font-bold">{b.people_count}</span> {t.beneficiaries.fields.people_count.toLowerCase()}</span>
                  </div>
                </div>

                <div className="mt-2.5">
                  <ProgressBar value={b.progress || 0} tid={`progress-mobile-${b.id}`} />
                </div>
              </button>

              <div className="flex items-center gap-1 px-3 pb-3" onClick={(e) => e.stopPropagation()}>
                <Link to={`/w/${b.id}`} data-testid={`share-mobile-${b.id}`} className="inline-flex items-center gap-1 flat-btn bg-white rounded-full px-3 py-1 text-[11px] font-semibold">
                  <Share2 className="w-3 h-3" strokeWidth={2} /> {t.beneficiaries.share}
                </Link>
                {user && (
                  <>
                    <button onClick={() => openEdit(b)} data-testid={`edit-mobile-${b.id}`} className="p-1.5 hover:text-[#670003] ml-auto" aria-label="Edit"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(b.id)} data-testid={`delete-mobile-${b.id}`} className="p-1.5 hover:text-[#670003]" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
                  </>
                )}
              </div>

              {isOpen && (
                <div className="px-4 pb-4 pt-1 border-t border-[#141414]/10 bg-[#fff0d2]/30 space-y-4">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#141414]/60 mb-1">{t.beneficiaries.fields.details}</div>
                    <div className="text-sm">{b.details || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#141414]/60 mb-1">{t.beneficiaries.fields.help_provided}</div>
                    <div className="text-sm">{b.help_provided || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#141414]/60 mb-2">
                      {t.beneficiaries.fields.needs} · {b.needs_fulfilled}/{b.needs_total} lunas
                    </div>
                    <div className="space-y-2">
                      {(b.needs || []).length === 0 && <div className="text-sm text-[#141414]/50">—</div>}
                      {(b.needs || []).map((n, i) => {
                        const rq = Number(String(n.requested_qty).replace(/[^0-9.]/g, "")) || 0;
                        const fq = Number(String(n.fulfilled_qty).replace(/[^0-9.]/g, "")) || 0;
                        const pct = rq > 0 ? Math.min(100, (fq / rq) * 100) : (n.status === "fulfilled" ? 100 : n.status === "partial" ? 50 : 0);
                        return (
                          <div key={i} data-testid={`need-mobile-${b.id}-${i}`} className="flat-border rounded-lg bg-white p-2.5">
                            <div className="flex items-center gap-2 mb-1.5">
                              <NeedStatusIcon s={n.status} />
                              <div className="font-semibold text-sm flex-1 min-w-0 truncate">{n.name}</div>
                              <div className="text-[11px] font-mono text-[#141414]/70 shrink-0">{n.fulfilled_qty}/{n.requested_qty}</div>
                            </div>
                            <div className="h-1.5 progress-track">
                              <div className="h-full bg-[#670003]" style={{ width: `${pct}%` }} />
                            </div>
                            <div className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-[#670003]">{t.beneficiaries.needStatus[n.status]}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Desktop: table */}
      <div className="hidden md:block flat-border rounded-2xl bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#141414]/15 bg-[#fff0d2]">
              <th className="p-3 w-8"></th>
              <th className="p-3">{th(t.beneficiaries.fields.pic_name, "pic_name", "pic")}</th>
              <th className="p-3 hidden md:table-cell">{th(t.beneficiaries.fields.kabupaten, "kabupaten", "kabupaten")}</th>
              <th className="p-3 hidden lg:table-cell">{th(t.beneficiaries.fields.kota, "kota", "kota")}</th>
              <th className="p-3 hidden sm:table-cell">{th(t.beneficiaries.fields.people_count, "people_count", "people")}</th>
              <th className="p-3">{th(t.beneficiaries.fields.progress, "progress", "progress")}</th>
              <th className="p-3"><span className="text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-[#141414]/70">{t.beneficiaries.fields.status}</span></th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={8} className="text-center py-12 text-[#141414]/50">{t.beneficiaries.empty}</td></tr>
            )}
            {rows.map((b) => (
              <Fragment key={b.id}>
                <tr data-testid={`row-${b.id}`} className="border-b border-[#141414]/10 hover:bg-[#fff0d2]/50 cursor-pointer" onClick={() => setExpanded((e) => ({ ...e, [b.id]: !e[b.id] }))}>
                  <td className="p-3 align-middle">
                    {expanded[b.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </td>
                  <td className="p-3">
                    <div className="font-semibold">{b.pic_name}</div>
                    <div className="text-[11px] text-[#141414]/60">{b.pic_number}</div>
                  </td>
                  <td className="p-3 hidden md:table-cell">{b.kabupaten}</td>
                  <td className="p-3 hidden lg:table-cell">{b.kota}</td>
                  <td className="p-3 hidden sm:table-cell font-mono">{b.people_count}</td>
                  <td className="p-3"><ProgressBar value={b.progress || 0} tid={`progress-${b.id}`} /></td>
                  <td className="p-3"><StatusPill s={b.status} t={t} statuses={statuses} lang={lang} /></td>
                  <td className="p-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <Link to={`/w/${b.id}`} data-testid={`share-${b.id}`} className="inline-flex items-center gap-1 flat-btn bg-white rounded-full px-2.5 py-1 text-[11px] font-semibold" aria-label={t.beneficiaries.share}>
                      <Share2 className="w-3 h-3" strokeWidth={2} /> <span className="hidden md:inline">{t.beneficiaries.share}</span>
                    </Link>
                    {user && (
                      <>
                        <button onClick={() => openEdit(b)} data-testid={`edit-${b.id}`} className="p-1.5 hover:text-[#670003] ml-1" aria-label="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                        <button onClick={() => remove(b.id)} data-testid={`delete-${b.id}`} className="p-1.5 hover:text-[#670003]" aria-label="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                      </>
                    )}
                  </td>
                </tr>
                {expanded[b.id] && (
                  <tr className="border-b border-[#141414]/10 bg-[#fff0d2]/30">
                    <td colSpan={8} className="p-5">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#141414]/60 mb-2">{t.beneficiaries.fields.details}</div>
                          <div className="text-sm mb-4">{b.details || "—"}</div>
                          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#141414]/60 mb-2">{t.beneficiaries.fields.help_provided}</div>
                          <div className="text-sm">{b.help_provided || "—"}</div>
                        </div>
                        <div className="md:col-span-2">
                          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#141414]/60 mb-2">
                            {t.beneficiaries.fields.needs} · {b.needs_fulfilled}/{b.needs_total} lunas
                          </div>
                          <div className="space-y-2">
                            {(b.needs || []).length === 0 && <div className="text-sm text-[#141414]/50">—</div>}
                            {(b.needs || []).map((n, i) => {
                              const rq = Number(String(n.requested_qty).replace(/[^0-9.]/g, "")) || 0;
                              const fq = Number(String(n.fulfilled_qty).replace(/[^0-9.]/g, "")) || 0;
                              const pct = rq > 0 ? Math.min(100, (fq / rq) * 100) : (n.status === "fulfilled" ? 100 : n.status === "partial" ? 50 : 0);
                              return (
                                <div key={i} data-testid={`need-${b.id}-${i}`} className="flat-border rounded-lg bg-white p-3">
                                  <div className="flex items-center gap-2 mb-1.5">
                                    <NeedStatusIcon s={n.status} />
                                    <div className="font-semibold text-sm flex-1">{n.name}</div>
                                    <div className="text-xs font-mono text-[#141414]/70">{n.fulfilled_qty} / {n.requested_qty}</div>
                                    <span className="text-[10px] font-semibold uppercase tracking-wider text-[#670003] w-16 text-right">{t.beneficiaries.needStatus[n.status]}</span>
                                  </div>
                                  <div className="h-1.5 progress-track">
                                    <div className="h-full bg-[#670003]" style={{ width: `${pct}%` }} />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Editor */}
      {editing && (
        <div className="fixed inset-0 bg-[#141414]/60 z-50 flex items-center justify-center p-4" onClick={close}>
          <div className="bg-white flat-border-2 rounded-2xl p-6 w-full max-w-3xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <div className="font-display text-2xl">{editing === "new" ? t.beneficiaries.add : t.admin.edit}</div>
              <button onClick={close} data-testid="modal-close" className="flat-btn bg-transparent rounded-full p-1.5"><X className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[["kabupaten", "text"], ["kota", "text"], ["pic_name", "text"], ["pic_number", "text"], ["people_count", "number"]].map(([k, ty]) => (
                <div key={k}>
                  <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#141414]/60">{t.beneficiaries.fields[k]}</label>
                  <input type={ty} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`field-${k}`} className="mt-1 w-full flat-border rounded-lg px-3 py-2 outline-none focus:border-[#670003]" />
                </div>
              ))}
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#141414]/60">{t.beneficiaries.fields.status}</label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="field-status" className="mt-1 w-full flat-border rounded-lg px-3 py-2 outline-none bg-white">
                  {statuses.map((s) => <option key={s.key} value={s.key}>{lang === "id" ? s.label_id : s.label_en}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#141414]/60">{t.beneficiaries.fields.details}</label>
                <textarea value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} rows={2} data-testid="field-details" className="mt-1 w-full flat-border rounded-lg px-3 py-2 outline-none" />
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#141414]/60">{t.beneficiaries.fields.help_provided}</label>
                <textarea value={form.help_provided} onChange={(e) => setForm({ ...form, help_provided: e.target.value })} rows={2} data-testid="field-help" className="mt-1 w-full flat-border rounded-lg px-3 py-2 outline-none" />
              </div>
            </div>

            {/* Needs editor */}
            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#141414]/60">{t.beneficiaries.fields.needs}</div>
                <button type="button" onClick={addNeedRow} data-testid="add-need" className="flat-btn bg-transparent rounded-full px-3 py-1 text-xs font-semibold flex items-center gap-1">
                  <Plus className="w-3 h-3" /> {t.beneficiaries.addNeed}
                </button>
              </div>
              <div className="space-y-2">
                {form.needs.map((n, i) => (
                  <div key={i} className="flat-border rounded-lg p-2 grid grid-cols-12 gap-2 items-center">
                    <input value={n.name} onChange={(e) => setNeed(i, { name: e.target.value })} placeholder="Nama kebutuhan" data-testid={`need-name-${i}`} className="col-span-12 md:col-span-5 flat-border rounded-md px-2 py-1.5 text-sm outline-none" />
                    <input value={n.requested_qty} onChange={(e) => setNeed(i, { requested_qty: e.target.value })} placeholder={t.beneficiaries.requestedQty} data-testid={`need-req-${i}`} className="col-span-4 md:col-span-2 flat-border rounded-md px-2 py-1.5 text-sm outline-none" />
                    <input value={n.fulfilled_qty} onChange={(e) => setNeed(i, { fulfilled_qty: e.target.value })} placeholder={t.beneficiaries.fulfilledQty} data-testid={`need-ful-${i}`} className="col-span-4 md:col-span-2 flat-border rounded-md px-2 py-1.5 text-sm outline-none" />
                    <select value={n.status} onChange={(e) => setNeed(i, { status: e.target.value })} data-testid={`need-status-${i}`} className="col-span-3 md:col-span-2 flat-border rounded-md px-2 py-1.5 text-sm outline-none bg-white">
                      <option value="pending">{t.beneficiaries.needStatus.pending}</option>
                      <option value="partial">{t.beneficiaries.needStatus.partial}</option>
                      <option value="fulfilled">{t.beneficiaries.needStatus.fulfilled}</option>
                    </select>
                    <button type="button" onClick={() => removeNeedRow(i)} data-testid={`need-remove-${i}`} className="col-span-1 text-[#670003] hover:text-[#141414]"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
                {form.needs.length === 0 && <div className="text-sm text-[#141414]/50">—</div>}
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <button onClick={save} disabled={loading} data-testid="save-btn" className="flat-btn-primary rounded-full px-5 py-2.5 font-semibold text-sm flex items-center gap-2">
                <Save className="w-4 h-4" strokeWidth={2.5} /> {t.admin.save}
              </button>
              <button onClick={close} className="flat-btn bg-transparent rounded-full px-5 py-2.5 font-semibold text-sm">{t.admin.cancel}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
